<template>
  <v-container max-width="80%" class="mx-auto py-8">
    <div class="text-overline font-weight-semibold mb-1">Submit a New Listing</div>
    <h2 class="text-h5 font-weight-bold mb-6">Enter Sailing Details</h2>

    <v-row align="start" class="my-row" style="--v-gutter-x: 18px; --v-gutter-y: 28px;">
      <!-- Right content -->
      <v-col cols="12" md="12">

        <!-- Progress -->
        <v-card class="mb-6" elevation="2" rounded="lg">
          <v-card-text>
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-subtitle-1 font-weight-medium">Step {{ currentStep }} of {{ totalSteps }}</div>
              <div class="text-caption">{{ Math.round(progressPercentage) }}%</div>
            </div>
            <v-progress-linear :model-value="progressPercentage" height="12" rounded color="secondary" />
          </v-card-text>
        </v-card>

        <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
          <!-- Left: title + subtitle -->
          <div class="d-flex flex-column mr-4">
            <h3 class="text-h6 font-weight-bold m-0">{{ foundItinerary.title }}</h3>
            <span class="text-overline text-medium-emphasis">{{ foundItinerary.ship_name }}</span>
          </div>

          <!-- Right: button -->
          <v-btn variant="tonal" prepend-icon="mdi-ferry" @click="showPorts = true">
            Ports & Itinerary
          </v-btn>
        </div>

        <!-- Step 1: Cabin Details -->
        <v-card v-show="currentStep === 1" class="mb-6" elevation="3" rounded="lg">
          <v-card-title class="text-subtitle-1 font-weight-bold">Cabin Details</v-card-title>
          <v-divider />
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select v-model="Listing.selectedCode" :items="cabinCodes" item-title="categoryCode"
                  item-value="categoryCode" label="Cabin Code" variant="outlined" density="comfortable" clearable />
              </v-col>

              <v-col cols="12" md="9">
                <v-text-field v-model="Listing.description" label="Description" variant="outlined" density="comfortable"
                  :hint="Listing.roomType ? `Room Type: ${Listing.roomType}` : ''" persistent-hint clearable />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field v-model="Listing.cabinNumber" label="Cabin Number"
                  placeholder="Enter Cabin Number (GTY if not assigned)" variant="outlined" density="comfortable"
                  clearable />
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field v-model.number="Listing.guestLimit" label="Max # Cabin Occupancy" type="number" min="1" max="8"
                  step="1" variant="outlined" density="comfortable" />
              </v-col>

              <v-col cols="12" md="3">
                <v-select v-model="Listing.tipsIncluded" :items="yesNo" label="Gratuities Included?" variant="outlined"
                  density="comfortable" />
              </v-col>

              <v-col cols="12" md="3">
                <v-select v-model="Listing.nameSwaps" :items="nameOptions" label="Name Swaps Approved?"
                  variant="outlined" density="comfortable" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Step 2: Pricing Breakdown -->
        <v-card v-show="currentStep === 2" class="mb-6" elevation="3" rounded="lg">
          <v-card-title class="text-subtitle-1 font-weight-bold">Pricing Breakdown</v-card-title>
          <v-divider />
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="Listing.cruiseFare" label="Cruise Fare (excl. taxes & fees)" type="number"
                  min="0" prepend-inner-icon="mdi-currency-usd" variant="outlined" density="comfortable" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="Listing.taxes" label="Port Taxes & Fees" type="number" min="0"
                  prepend-inner-icon="mdi-currency-usd" variant="outlined" density="comfortable" />
              </v-col>
            </v-row>

            <v-alert type="info" variant="tonal" border="start" class="my-4">
              Travelers compare your listing to public prices. A competitive fare increases your chances of booking.
            </v-alert>

            <v-row dense class="mb-2">
              <v-col cols="12">
                <div class="text-subtitle-2 mb-2">Additional Discount or Perks</div>

                <!-- Chips -->
                <v-chip-group ref="perksGroup" v-model="Listing.perks" multiple selected-class="text-white" show-arrows
                  center-active @update:modelValue="onPerksChange">
                  <!-- Predefined (except Other) -->
                  <v-chip v-for="opt in options.filter(o => o.value !== 'other')" :key="opt.value" :value="opt.value"
                    variant="elevated" filter class="ma-1">
                    {{ opt.label }}
                  </v-chip>

                  <!-- User-added (before Other) -->
                  <v-chip v-for="opt in customOptions" :key="opt.value" :value="opt.value" variant="elevated" filter
                    class="ma-1" closable @click:close="removeCustom(opt.value)">
                    {{ opt.label }}
                  </v-chip>

                  <!-- Other last -->
                  <v-chip :key="'other'" value="other" variant="elevated" filter class="ma-1">
                    Other
                  </v-chip>
                </v-chip-group>

                <!-- "Other" input (appears after tapping Other) -->
                <div v-if="showCustomInput" class="mt-2">
                  <v-text-field v-model="customPerkText" label="Describe the perk"
                    placeholder="e.g., Free photo package" variant="outlined" density="comfortable"
                    :rules="[v => !!v?.trim() || 'Enter a perk']" @keyup.enter="addCustomPerk">
                    <template #append-inner>
                      <v-btn class="ml-2" height="40" :disabled="!customPerkText.trim()" @click="addCustomPerk">
                        Add
                      </v-btn>
                    </template>
                  </v-text-field>
                </div>

              </v-col>
            </v-row>

            <v-row dense justify="center">
              <v-col cols="12" sm="6" md="6">
                <v-text-field :model-value="getTotal" label="Total Fare" readonly prepend-inner-icon="mdi-currency-usd"
                  variant="outlined" density="comfortable" />
                <div class="text-h6	text-error text-center mt-1">
                  Note: Service fees are not included here.
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Step 3: Payment Terms -->
        <v-card v-show="currentStep === 3" class="mb-6" elevation="3" rounded="lg">
          <v-card-title class="text-subtitle-1 font-weight-bold">Payment Terms</v-card-title>
          <v-divider />
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="Listing.deposit" label="Deposit Amount" type="number" min="0"
                  prepend-inner-icon="mdi-currency-usd" variant="outlined" density="comfortable" class="mb-4">
                  <!-- Info tooltip icon on the right -->
                  <template #append-inner>
                    <v-tooltip text="The date the supplier requires full payment by" location="top">
                      <template #activator="{ props: tip }">
                        <v-btn v-bind="tip" icon variant="text" size="small" class="ml-1"
                          aria-label="Final payment info" @click.stop>
                          <v-icon icon="mdi-information-outline"></v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                  </template>
                </v-text-field>

                <!-- Final Payment Date -->
                <v-dialog v-model="finalDialog" max-width="360">
                  <template #activator="{ props }">
                    <v-text-field v-bind="props" :model-value="Listing.finalPaymentDate" label="Final Payment Date"
                      variant="outlined" density="comfortable" prepend-inner-icon="mdi-calendar" readonly clearable
                      :error="finalPaymentTooLate" :messages="finalPaymentTooLate ? [`Must be ≤ ${finalMaxIso}`] : []"
                      @click:clear="Listing.finalPaymentDate = ''">
                      <!-- Info tooltip icon on the right -->
                      <template #append-inner>
                        <v-tooltip text="Pick the date the supplier requires full payment by" location="top">
                          <template #activator="{ props: tip }">
                            <v-btn v-bind="tip" icon variant="text" size="small" class="ml-1"
                              aria-label="Final payment info" @click.stop>
                              <v-icon icon="mdi-information-outline"></v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </template>

                  <v-card>
                    <v-card-title class="text-subtitle-1 font-weight-bold">
                      Select final payment date
                    </v-card-title>
                    <v-divider />
                    <v-card-text>
                      <v-date-picker v-model="tempFinal" :min="todayIso()" :max="finalMaxIso || undefined" hide-header
                        @update:model-value="val => (tempFinal = normalizeIso(val))" />
                      <div v-if="finalMaxIso" class="text-caption mt-2 text-medium-emphasis">
                        Latest allowed date: <strong>{{ finalMaxIso }}</strong>
                      </div>
                    </v-card-text>
                    <v-divider />
                    <v-card-actions class="justify-end">
                      <v-btn variant="text" @click="finalDialog = false">Cancel</v-btn>
                      <v-btn color="primary" :disabled="!tempFinal" @click="saveFinalPayment">
                        Save
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>



                <!-- Remove Listing On -->
                <v-dialog v-model="removeDialog" max-width="360">
                  <template #activator="{ props }">
                    <v-text-field v-bind="props" :model-value="Listing.removeDate" label="Remove Listing On"
                      variant="outlined" density="comfortable" prepend-inner-icon="mdi-calendar" readonly clearable
                      @click:clear="Listing.removeDate = ''" class="mt-4">
                      <!-- Info tooltip icon on the right -->
                      <template #append-inner>
                        <v-tooltip text="Pick the date you want us to remove the listing from the marketplace"
                          location="top">
                          <template #activator="{ props: tip }">
                            <v-btn v-bind="tip" icon variant="text" size="small" class="ml-1"
                              aria-label="Final payment info" @click.stop>
                              <v-icon icon="mdi-information-outline"></v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </template>
                  <v-card>
                    <v-card-title class="text-subtitle-1 font-weight-bold">Select removal date</v-card-title>
                    <v-divider />
                    <v-card-text>
                      <v-date-picker v-model="tempRemove" :min="todayIso()" hide-header
                        @update:model-value="val => tempRemove = normalizeIso(val)" />
                    </v-card-text>
                    <v-divider />
                    <v-card-actions class="justify-end">
                      <v-btn variant="text" @click="removeDialog = false">Cancel</v-btn>
                      <v-btn color="primary" @click="Listing.removeDate = tempRemove; removeDialog = false"
                        :disabled="!tempRemove">
                        Save
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-col>

              <!-- Cancellation table -->
              <v-col cols="12" md="8">
                <div class="text-subtitle-2 mb-2">Cancellation Schedule with Penalty Percents</div>

                <div class="d-flex justify-center my-2">
                  <v-alert type="info" variant="tonal" border="start" class="w-100" density="comfortable">
                    Cancellation policy and deposit amounts must match supplier policies and what will appear on the
                    booking
                    invoice
                  </v-alert>
                </div>

                <v-table density="comfortable" class="rounded-lg elevation-1">
                  <thead>
                    <tr class="text-center">
                      <th class="text-left">Type</th>
                      <th class="text-left">Date</th>
                      <th class="text-left">Percentage</th>
                      <th class="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in Listing.cancellation" :key="index">
                      <td class="text-right font-weight-bold" style="width: 220px;">
                        {{ getLabel(index) }}
                      </td>

                      <!-- Date cell -->
                      <td style="min-width: 220px;">
                        <v-dialog v-model="cancelDialog[index]" max-width="360">
                          <template #activator="{ props }">
                            <v-text-field v-bind="props" :model-value="row.date" label="Select date" variant="outlined"
                              density="comfortable" readonly clearable :error="isDateInvalid(index)"
                              :messages="isDateInvalid(index) ? ['Date must be in valid order'] : []"
                              @click:clear="Listing.cancellation[index].date = ''">
                              <!-- optional calendar icon on the right -->
                              <template #append-inner>
                                <v-icon>mdi-calendar</v-icon>
                              </template>
                            </v-text-field>
                          </template>

                          <v-card>
                            <v-card-title class="text-subtitle-1 font-weight-bold">
                              Pick a date ({{ getLabel(index) }})
                            </v-card-title>
                            <v-divider />
                            <v-card-text>
                              <v-date-picker v-model="cancelTemp[index]" hide-header
                                @update:model-value="val => cancelTemp[index] = normalizeIso(val)" />
                            </v-card-text>
                            <v-divider />
                            <v-card-actions class="justify-end">
                              <v-btn variant="text" @click="cancelDialog[index] = false">Cancel</v-btn>
                              <v-btn color="primary" :disabled="!cancelTemp[index]" @click="saveCancelDate(index)">
                                Save
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </v-dialog>
                      </td>

                      <td style="min-width: 180px;">
                        <v-text-field v-model.number="row.percentage" type="number" min="0" max="100" suffix="%"
                          variant="outlined" density="comfortable" />
                      </td>
                      <td class="text-center" style="width: 120px;">
                        <v-btn v-if="canRemove(index)" color="error" variant="outlined" size="small"
                          @click="removeRow(index)">
                          Remove
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-table>

                <div class="d-flex justify-center mt-3">
                  <v-btn color="success" @click="addMiddleRow" prepend-icon="mdi-plus">
                    Add "On and Before" Row
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Step 4: Final Details -->
        <v-card v-show="currentStep === 4" class="mb-6" elevation="3" rounded="lg">
          <v-card-title class="text-subtitle-1 font-weight-bold">Final Details</v-card-title>
          <v-divider />
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-textarea v-model="Listing.notes"
                  label="Please share anything additional our team should be aware of or special requirements for this booking"
                  placeholder="Enter any special requests or notes here..." rows="4" auto-grow variant="outlined"
                  density="comfortable" />
              </v-col>

              <v-col cols="12" class="mt-2">
                <div class="text-subtitle-1 mb-2 text-center">I would like to promote this listing:</div>

                <!-- Center the group -->
                <div class="d-flex justify-center">
                  <v-btn-toggle v-model="Listing.promote" color="primary" rounded="lg" mandatory class="w-100"
                    style="max-width: 420px;">
                    <!-- Wider, equal-width buttons -->
                    <v-btn :value="true" class="flex-grow-1 px-8" size="large" prepend-icon="mdi-check">Yes</v-btn>
                    <v-btn :value="false" class="flex-grow-1 px-8" size="large" prepend-icon="mdi-close">No</v-btn>
                  </v-btn-toggle>
                </div>
                <!-- Info banner -->
                <div class="d-flex justify-center mt-2">
                  <v-alert type="info" variant="tonal" border="start" class="w-100" style="max-width: 420px;"
                    density="comfortable">
                    Choosing “Yes” adds an additional 1.5%.
                  </v-alert>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Nav buttons -->
        <div class="d-flex justify-space-between mt-4 align-center">
          <v-btn variant="outlined" size="large" :disabled="currentStep === 1" @click="currentStep--">
            Previous
          </v-btn>

          <div class="d-flex flex-column align-end">
            <v-btn color="primary" size="large" class="nav-next" :disabled="!isCurrentStepValid" @click="nextStep">
              {{ currentStep === totalSteps ? 'Done' : 'Next' }}
            </v-btn>
            <small v-if="!isCurrentStepValid" class="mt-1 text-error">
              Please complete all required fields for this step.
            </small>
          </div>
        </div>

      </v-col>
    </v-row>

    <!-- Slide-in overlay drawer -->
    <v-navigation-drawer v-model="showPorts" location="left" temporary width="580" scrim class="ports-drawer">
      <div class="d-flex align-center justify-space-between pa-3">
        <div class="text-h5 font-weight-bold text-center">Itinerary & Ports</div>
        <v-btn icon @click="showPorts = false"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-divider />
      <div class="pa-3">
        <PortStopsCard :foundItinerary="foundItinerary" />
      </div>
    </v-navigation-drawer>

  </v-container>
</template>

<script>
import PortStopsCard from '@/components/PortStopsCard.vue';
import { supabase } from "@/supabaseClient";

export default {
  name: 'StepTwoVuetify',
  components: { PortStopsCard },
  props: {
    foundItinerary: { type: Object, required: true }
  },
  data() {
    return {
      currentStep: 1,
      totalSteps: 4,
      showPorts: false,

      // dialogs for date pickers
      finalDialog: false,
      removeDialog: false,
      tempFinal: null,
      tempRemove: null,

      // cabin types
      cabins: [],
      cabinCodes: [], // Array of just codes

      // perks options
      options: [
        { label: 'Onboard Credit - $50', value: 'onboardCredit50' },
        { label: 'Onboard Credit - $100', value: 'onboardCredit100' },
        { label: 'Specialty Dining', value: 'specialtyDining' },
        { label: 'Prepaid Gratuity', value: 'prepaidGratuity' },
        { label: 'Beverage Package', value: 'beveragePackage' },
        { label: 'Internet Package', value: 'internetPackage' },
        { label: 'Shore Excursion Credit', value: 'shoreExcursionCredit' },
        // The trigger chip:
        { label: 'Other', value: 'other' },
      ],
      customOptions: [],
      showCustomInput: false,
      customPerkText: '',

      yesNo: ['Yes', 'No'],
      nameOptions: ['Yes', 'Yes with restrictions', 'No'],

      cancelDialog: [],   // boolean per row
      cancelTemp: [],     // temp date per row ('YYYY-MM-DD')

      Listing: {
        selectedCode: '',
        description: '',
        roomType: '',
        cabinNumber: '',
        guestLimit: '',
        tipsIncluded: 'No',
        nameSwaps: 'No',
        cruiseFare: '',
        taxes: '',
        perks: [],
        totalCost: '',
        deposit: '',
        finalPaymentDate: '',
        cancellation: [
          { date: '', percentage: '' }, // Before Date
          { date: '', percentage: '' }, // After Date
        ],
        removeDate: '',
        notes: '',
        promote: true,
        sailingID: '',
        itinerary: null,
      },
    };
  },
  computed: {
    getTotal() {
      const fare = Number(this.Listing.cruiseFare) || 0;
      const taxes = Number(this.Listing.taxes) || 0;
      return (fare + taxes).toFixed(2);
    },
    progressPercentage() {
      return (this.currentStep / this.totalSteps) * 100;
    },
    sailingDateIso() {
      const it = this.foundItinerary || this.Listing.itinerary || {}
      const raw =
        it.date || it.sailDate || it.departureDate || it.startDate || ''
      return this.normalizeIso(raw)
    },
    finalMaxIso() {
      // latest allowed final payment date = sailing - 30 days
      return this.sailingDateIso ? this.isoMinusDays(this.sailingDateIso, 30) : ''
    },
    finalPaymentTooLate() {
      if (!this.Listing.finalPaymentDate || !this.finalMaxIso) return false
      return new Date(this.Listing.finalPaymentDate) > new Date(this.finalMaxIso)
    },
    step1Valid() {
      return !!(
        this.Listing.selectedCode &&
        (this.Listing.description || '').trim() &&
        (this.Listing.cabinNumber || '').trim() &&
        Number(this.Listing.guestLimit) >= 1 &&
        this.Listing.tipsIncluded &&
        this.Listing.nameSwaps
      )
    },
    step2Valid() {
      return this.isNonNegNumber(this.Listing.cruiseFare) &&
        this.isNonNegNumber(this.Listing.taxes)
    },
    step3Valid() {
      // deposit + final/removal dates
      if (!this.isNonNegNumber(this.Listing.deposit)) return false
      if (!this.Listing.finalPaymentDate) return false
      if (this.finalMaxIso && this.Listing.finalPaymentDate > this.finalMaxIso) return false
      if (!this.Listing.removeDate) return false

      // cancellation rows: every row needs date + % between 0..100 and no ordering errors
      const rows = this.Listing.cancellation || []
      if (!rows.length) return false
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i]
        const hasDate = !!r.date
        const pct = Number(r.percentage)
        const hasPct = r.percentage !== '' && !isNaN(pct) && pct >= 0 && pct <= 100
        if (!hasDate || !hasPct || this.isDateInvalid(i)) return false
      }
      return true
    },
    // Step 4 currently has no required inputs — allow Done by default.
    // If you want to require notes, change to: return !!(this.Listing.notes || '').trim()
    step4Valid() { return true },

    isCurrentStepValid() {
      switch (this.currentStep) {
        case 1: return this.step1Valid
        case 2: return this.step2Valid
        case 3: return this.step3Valid
        case 4: return this.step4Valid
        default: return false
      }
    },

  },
  created() {
    this.loadCabins();
    // carry forward from Step 1
    this.Listing.sailingID = this.foundItinerary?.sailingID || '';
    this.Listing.itinerary = this.foundItinerary || null;
    this.syncCancelPickers()
  },
  methods: {
    async loadCabins() {
      try {
        this.cabins = [];

        const cruiseLine = this.foundItinerary?.ship?.cruiseLine ?? '';
        if (!cruiseLine) {
          console.warn('No cruise line found on itinerary.');
          return;
        }

        let query = supabase
          .from('cabin_categories')
          .select('code,name,rank,type,is_active,meta')
          .eq('cruise_line', cruiseLine)
          .eq('is_active', true)
          .order('rank', { ascending: true })
          .order('name', { ascending: true });


        const { data, error } = await query;

        if (error) {
          console.error('Error loading cabins:', error);
          return;
        }

        const rows = Array.isArray(data) ? data : [];
        this.cabins = rows;

        // Build array of just codes
        this.cabinCodes = (data ?? []).map(row => row.code);


      } catch (e) {
        console.error('Error loading cabins:', e);
      }
    },

    todayIso() {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    normalizeIso(val) {
      if (!val) return '';
      if (typeof val === 'string') return val.slice(0, 10);
      const d = new Date(val);
      if (isNaN(d)) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },

    // Perks
    onPerksChange(values) {
      // If "other" was selected, show the input and unselect it so model only stores real perks
      if (values.includes('other')) {
        this.showCustomInput = true;
        this.Listing.perks = values.filter(v => v !== 'other');
      }
    },
    addCustomPerk() {
      const label = (this.customPerkText || '').trim();
      if (!label) return;

      const value = this.slugify(label);

      // avoid duplicates against predefined and custom
      const exists =
        this.options.some(o => o.value === value) ||
        this.customOptions.some(o => o.value === value);

      if (!exists) this.customOptions.push({ label, value });

      // select it
      if (!this.Listing.perks.includes(value)) this.Listing.perks.push(value);

      // reset input (keep open if you want multiple entries)
      this.customPerkText = '';
      this.showCustomInput = false;

      // ensure it’s visible & right arrow becomes active
      this.$nextTick(() => this.scrollPerksToEnd());
    },

    scrollPerksToEnd() {
      // v-chip-group extends v-slide-group; grab its scrolling container
      const root = this.$refs.perksGroup?.$el;
      const container = root?.querySelector?.('.v-slide-group__container');

      if (container) {
        container.scrollTo({
          left: container.scrollWidth,
          behavior: 'smooth',
        });
      } else {
        // fallback: try calling next() if available
        this.$refs.perksGroup?.next?.();
      }
    },

    removeCustom(value) {
      this.Listing.perks = this.Listing.perks.filter(v => v !== value);
      this.customOptions = this.customOptions.filter(o => o.value !== value);
    },
    slugify(str) {
      return str
        .toLowerCase()
        .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-{2,}/g, '-');
    },

    // Steps
    nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      } else {
        this.$emit('next', this.Listing);
      }
    },

    // Cancellation table helpers + validation
    addMiddleRow() {
      this.Listing.cancellation.splice(this.Listing.cancellation.length - 1, 0, { date: '', percentage: '' });
    },
    canRemove(index) {
      return index !== 0 && index !== this.Listing.cancellation.length - 1;
    },
    removeRow(index) {
      if (this.canRemove(index)) {
        this.Listing.cancellation.splice(index, 1);
      }
    },
    getLabel(index) {
      if (index === 0) return 'Before Date';
      if (index === this.Listing.cancellation.length - 1) return 'After Date';
      return 'On and Before';
    },
    isDateInvalid(index) {
      const rows = this.Listing.cancellation;
      const currentDate = new Date(rows[index]?.date);
      if (!rows[index].date) return false;

      const beforeDate = rows[0]?.date ? new Date(rows[0].date) : null;
      const afterDate = rows[rows.length - 1]?.date ? new Date(rows[rows.length - 1].date) : null;

      if (index === 0 && afterDate) return currentDate >= afterDate;
      if (index === rows.length - 1 && beforeDate) return currentDate <= beforeDate;
      if (beforeDate && afterDate) return currentDate <= beforeDate || currentDate >= afterDate;
      return false;
    },
    fixInvalidDates() {
      const rows = this.Listing.cancellation;
      const beforeIndex = 0;
      const afterIndex = rows.length - 1;

      const before = rows[beforeIndex];
      const after = rows[afterIndex];

      if (before.date && after.date) {
        const beforeDate = new Date(before.date);
        const afterDate = new Date(after.date);

        if (beforeDate >= afterDate) {
          const newAfterDate = new Date(beforeDate);
          newAfterDate.setDate(newAfterDate.getDate() + 1);
          this.Listing.cancellation[afterIndex].date = newAfterDate.toISOString().slice(0, 10);
        }

        if (afterDate <= beforeDate) {
          const newBeforeDate = new Date(afterDate);
          newBeforeDate.setDate(newBeforeDate.getDate() - 1);
          this.Listing.cancellation[beforeIndex].date = newBeforeDate.toISOString().slice(0, 10);
        }
      }

      const beforeDate = before.date ? new Date(before.date) : null;
      const afterDate = after.date ? new Date(after.date) : null;

      for (let i = 1; i < afterIndex; i++) {
        const row = rows[i];
        if (!row.date) continue;
        const current = new Date(row.date);

        if (beforeDate && current <= beforeDate) {
          const nd = new Date(beforeDate);
          nd.setDate(nd.getDate() + 1);
          row.date = nd.toISOString().slice(0, 10);
        }
        if (afterDate && current >= afterDate) {
          const nd = new Date(afterDate);
          nd.setDate(nd.getDate() - 1);
          row.date = nd.toISOString().slice(0, 10);
        }
      }
    },
    syncCancelPickers() {
      const len = this.Listing.cancellation.length
      // resize without losing current values
      this.cancelDialog.length = len
      this.cancelTemp.length = len
      for (let i = 0; i < len; i++) {
        if (typeof this.cancelDialog[i] !== 'boolean') this.cancelDialog[i] = false
        if (!this.cancelTemp[i]) this.cancelTemp[i] = this.Listing.cancellation[i]?.date || ''
      }
    },

    saveCancelDate(index) {
      const val = this.cancelTemp[index]
      if (val) this.Listing.cancellation[index].date = this.normalizeIso(val)
      this.cancelDialog[index] = false
    },
    isoMinusDays(iso, days = 7) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d)) return ''
      d.setDate(d.getDate() - days)
      return d.toISOString().slice(0, 10)
    },
    saveFinalPayment() {
      const picked = this.normalizeIso(this.tempFinal)
      // block if past the max allowed date
      if (this.finalMaxIso && picked > this.finalMaxIso) {
        // Show a small hint; use your snackbar/toast if you prefer
        this.$nextTick(() =>
          alert(`Final payment must be on or before ${this.finalMaxIso}`)
        )
        return
      }
      this.Listing.finalPaymentDate = picked

      // Default remove date to 7 days prior if empty
      if (!this.Listing.removeDate) {
        this.Listing.removeDate = this.isoMinusDays(picked, 7)
        // keep remove picker in sync if you use one
        this.tempRemove = this.Listing.removeDate
      }
      this.finalDialog = false
    },
    isNonNegNumber(v) {
      if (v === '' || v === null || v === undefined) return false
      const n = Number(v)
      return !isNaN(n) && n >= 0
    },
  },
  watch: {
    'Listing.cancellation': {
      deep: true,
      handler() {
        this.fixInvalidDates();
      }
    },
    'Listing.selectedCode'(code) {
      const match = this.cabins.find(opt => opt.code === code);
      if (match) {
        this.Listing.description = match.name || '';
        this.Listing.roomType = match.type || '';
      } else {
        this.Listing.description = '';
        this.Listing.roomType = '';
      }
    },
    getTotal: {
      immediate: true,
      handler(val) {
        this.Listing.totalCost = val;
      }
    },
    // keep arrays in sync when rows are added/removed
    'Listing.cancellation.length'() {
      this.syncCancelPickers()
    },
    'Listing.finalPaymentDate'(val) {
      if (val && this.finalMaxIso && val > this.finalMaxIso) {
        this.Listing.finalPaymentDate = this.finalMaxIso
      }
      if (val && !this.Listing.removeDate) {
        this.Listing.removeDate = this.isoMinusDays(val, 7)
        this.tempRemove = this.Listing.removeDate
      }
    },
    // when the dialog opens, copy the current removeDate into the temp picker
    removeDialog(val) {
      if (val) {
        this.tempRemove = this.Listing.removeDate || ''
      }
    },

    // if removeDate changes anywhere, mirror it into the picker
    'Listing.removeDate'(val) {
      this.tempRemove = val || ''
    },
  }
};
</script>

<style scoped>
.nav-next {
  min-width: 160px;
}
</style>
